import { useTranslation } from 'react-i18next';

import type { ProductionState } from '@/hooks/useProduction';
import { cn } from '@/lib/utils';

function StatCard({ value, label, accent }: { value: React.ReactNode; label: string; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-elevated px-2 py-3 text-center">
      <div className={cn('font-mono text-base font-semibold', accent ? 'text-primary' : 'text-foreground')}>{value}</div>
      <div className="mt-1 text-[10px] leading-tight text-faint">{label}</div>
    </div>
  );
}

const formatTime = (s: number): string => {
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}m ${sec}s`;
};

/** Cost/time/throughput for the run, plus which model APIs it leaned on. */
export default function TelemetryPanel({ telemetry }: { telemetry: ProductionState['telemetry'] }) {
  const { t } = useTranslation('production');
  if (!telemetry) return null;

  const breakdown = Object.entries(telemetry.api_breakdown || {});

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        <StatCard value={formatTime(telemetry.total_duration_seconds ?? 0)} label={t('telemetry.totalTime')} accent />
        <StatCard value={telemetry.total_api_calls ?? 0} label={t('telemetry.apiCalls')} accent />
        <StatCard value={`$${(telemetry.total_estimated_cost_usd ?? 0).toFixed(2)}`} label={t('telemetry.estCost')} accent />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <StatCard
          value={`${telemetry.active_workers ?? 0}${telemetry.max_parallel_workers ? `/${telemetry.max_parallel_workers}` : ''}`}
          label={t('telemetry.inParallel')}
        />
        <StatCard value={telemetry.peak_workers ?? 0} label={t('telemetry.peakParallel')} />
      </div>
      {breakdown.length > 0 && (
        <div className="pt-1">
          {breakdown.map(([api, count]) => (
            <div
              key={api}
              className="flex justify-between border-b border-dashed border-border px-0.5 py-1.5 text-[11.5px] text-muted-foreground last:border-b-0"
            >
              <span>{api.replace(/_/g, ' ')}</span>
              <span className="font-mono text-faint">{count}x</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
